import { Component, inject, type OnInit, signal } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { UserRoles } from '@core/auth/roles';
import { EnvironmentService } from '@core/environment/environment.service';
import { NavbarMainRoutes, Routes } from '@core/routes/routes';
import { FinoCommonModule } from '@fino-ui/common';
import { FinoIconModule } from '@fino-ui/icon';
import { FinoTranslationModule, TranslateService } from '@fino-ui/translation';
import { CaptureTransactionService } from '@modules/capture-transaction/capture-transaction.service';
import { CaptureWarrantService } from '@modules/capture-warrant/capture-warrant.service';
import { CorrectionToolService } from '@modules/correction-tool/correction-tool.service';
import { IdentifyDocumentService } from '@modules/identify-document/identify-document.service';
import Keycloak from 'keycloak-js';
import { keys, map, some } from 'lodash';
import { LocalStorageService } from 'ngx-webstorage';
import type { Counter } from './navbar.model';
import { NavbarService } from './navbar.service';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.sass'],
  imports: [
    RouterLinkActive,
    RouterLink,
    FinoIconModule,
    FinoTranslationModule,
    FinoCommonModule,
  ],
})
export class NavbarComponent implements OnInit {
  private readonly identifyDocumentService = inject(IdentifyDocumentService);
  private readonly captureTransactionService = inject(
    CaptureTransactionService,
  );
  private readonly captureWarrantService = inject(CaptureWarrantService);
  private readonly correctionToolService = inject(CorrectionToolService);
  private readonly navbarService = inject(NavbarService);
  private readonly translateService = inject(TranslateService);
  private readonly storage = inject(LocalStorageService);
  private readonly environmentService = inject(EnvironmentService);

  private readonly authService = inject(Keycloak);

  public loadCounts = this.navbarService.loadCounts;
  public isUpright = false;

  counts: any = {};
  Roles = UserRoles;
  userProfile: any = {};

  routes = NavbarMainRoutes;

  counter = {
    identifyDocument: signal<Counter>({ open: 0, locked: 0 }),
    captureTransaction: signal<Counter>({ open: 0, locked: 0 }),
    captureTransactionQA: signal<Counter>({ open: 0, locked: 0 }),
    captureWarrant: signal<Counter>({ open: 0, locked: 0 }),
    correctionTool: signal<Counter>({ open: 0, locked: 0 }),
  };

  ngOnInit() {
    map(keys(this.counter), (jobType) => this.setNumberOfJobs(jobType));

    this.userProfile = this.authService.tokenParsed;
    this.loadCounts.subscribe((type: string) => this.setNumberOfJobs(type));
  }

  hasRole(role: string | undefined) {
    if (!role) return false;
    return this.authService.hasRealmRole(role);
  }

  hasOrganization(organizations: string[] | undefined) {
    if (!organizations || !this.userProfile) return false;
    return organizations.some((org) =>
      this.userProfile?.organization?.includes(org),
    );
  }

  shouldShowRoute(route: any): boolean {
    const hasAccess =
      some(route.roles, (r) => this.hasRole(r)) ||
      this.hasOrganization(route.organizations);

    if (route.noProd && this.environmentService.isProduction) return false;
    return hasAccess;
  }

  logout() {
    this.authService.logout();
  }

  toggleUpright() {
    this.isUpright = !this.isUpright;
    this.navbarService.toggleUpright.next(this.isUpright);
  }

  get clarificationRoute() {
    return Routes.Clarification;
  }

  get hasMoreDropwdown() {
    return this.hasRole(UserRoles.CLARIFICATION);
  }

  get currentLang() {
    return this.translateService.currentLang;
  }

  chooseLanguage(lang: string) {
    this.translateService.use(lang);
    this.storage.store('language', lang);
  }

  private loadNumberOfTicketsForJob(job: string) {
    switch (job) {
      case 'identifyDocument':
        return this.identifyDocumentService.getNumJobs();
      case 'captureTransaction':
        return this.captureTransactionService.getNumJobs();
      case 'captureTransactionQA':
        return this.captureTransactionService.getNumJobsQA();
      case 'captureWarrant':
        return this.captureWarrantService.getNumJobs();
      case 'correctionTool':
        return this.correctionToolService.getCount();
    }
  }

  setNumberOfJobs(type: string) {
    this.loadNumberOfTicketsForJob(type)?.subscribe((res: any) => {
      this.counter[type].set({
        locked: res.locked.TOTAL,
        open: res.open.TOTAL,
      });
    });
  }
}
